import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Popover, Typography, message } from 'antd'
import moment from 'moment'

import { FlagHandlingRow, getFlagHandlings, retractCase } from '../../../api/arendeApi'
import { useApi } from '../../../api/useApi'
import { getMe } from '../../../store/organizationSlice'
import { PermissionTypes } from '../../../components/menus/utils/generateSideMenuChildren'
import HandleFlagModal from '../../arende/components/handleFlag/HandleFlagModal'
import { KIND_LABEL, reasonLabel } from '../../arende/components/handleFlag/reasons'

const { Text, Title } = Typography

interface Props {
  /** Flaggans typ — `:flag` i rutten. */
  flagType: string
  /** Flaggans id — `:id` i rutten. */
  flagId: string
  /** Målets namn, för modalens rubrik. Saknas vid direktnavigering på URL. */
  targetNamn?: string
  nivå?: number
}

/**
 * Hanteringshistorik och Hantera-knapp på flaggans detaljvy (#30 PR 7).
 *
 * Varför historiken ligger HÄR och inte bara i Hanterade flaggor: den listan är
 * scope-filtrerad på ägare/tilldelad/team, och en hantering har varken tilldelad eller
 * team — bara sin ägare. En kollegas avfärdande blev därför osynligt för alla andra,
 * som såg symbolen i Status-kolumnen utan att kunna se vad som gjorts eller varför.
 *
 * På flaggans sida försvinner problemet av sig självt: har du kommit hit har du redan
 * passerat behörighetskontrollen för att se flaggan. "Ser du flaggan, ser du vad som
 * gjorts med den" behöver alltså ingen egen scope-fråga.
 *
 * YTAN ÄR BUDGETERAD. Sektionen bor i en smal spalt (25 %) och får kosta två rader per
 * hantering — vad och när, sedan av vem. Allt annat (skäl, kommentar, Ångra) ligger
 * bakom "Detaljer". Flaggsidans egentliga innehåll är motiveringen till vänster;
 * hanteringen ska svara på om någon redan tagit i den, inte konkurrera om läsningen.
 */
const FlagHandlings = ({ flagType, flagId, targetNamn, nivå }: Props) => {
  const api = useApi()
  const me = useSelector(getMe)
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [retracting, setRetracting] = useState<number | null>(null)
  const [öppenDetalj, setÖppenDetalj] = useState<number | null>(null)

  const nyckel = ['flagHandlings', flagType, flagId]
  const får = me.permissions?.includes(PermissionTypes.uppföljningslista)

  const { data: hanteringar } = useQuery(nyckel, () => getFlagHandlings(flagType, flagId), {
    // Hela hanterings-funktionen hänger på den här behörigheten: utan den kan man
    // varken hantera eller ångra, och backend nekar anropet med 403.
    enabled: får,
  })

  if (!får) return null

  const retract = async (id: number) => {
    setRetracting(id)

    try {
      await retractCase(api, id)
      await queryClient.invalidateQueries(nyckel)
      // Hanterade flaggor listar samma rader och blir annars kvar med den ångrade.
      await queryClient.invalidateQueries(['cases'])
      message.success('Hanteringen är ångrad')
    } catch (e) {
      message.error('Kunde inte ångra hanteringen')
    } finally {
      setRetracting(null)
    }
  }

  // Popovern stängs FÖRST. Antds popover ligger på z-index 1030 och modalen på 1000,
  // så dialogen hamnade bakom den. Att skruva upp modalens z-index hade dolt symptomet;
  // popovern har ändå inget att göra på skärmen när frågan ställs.
  const bekräftaÅngra = (h: FlagHandlingRow) => {
    setÖppenDetalj(null)

    // En flagga kan ha flera hanteringar. Läget räknar alla som inte är återtagna, så
    // att ångra en av två gör INTE flaggan ohanterad — den går bara från två till en.
    // Texten måste säga vilket av fallen det är, särskilt här där de andra
    // hanteringarna står listade några rader upp.
    const övriga = (hanteringar?.length ?? 1) - 1

    // Bulkhanteringen väger tyngst av de två upplysningarna: att ångra här släpper
    // ALLA flaggor i hanteringen, och det syns ingenstans när man står på en enskild
    // flagga. Då får nyansen om den här flaggans läge stå tillbaka — det viktiga är
    // att man inte oavsiktligt rör tolv andra.
    const flerFlaggor = h.antal_flaggor > 1

    Modal.confirm({
      title: 'Ångra hanteringen?',
      content: flerFlaggor
        ? `Hanteringen gäller ${h.antal_flaggor} flaggor och slutar gälla för alla — inte bara den här. Hanteringen raderas inte, den slutar bara gälla.`
        : övriga === 0
        ? 'Flaggan blir ohanterad igen. Hanteringen raderas inte, den slutar bara gälla.'
        : `Flaggan förblir hanterad — ${övriga === 1 ? 'en annan hantering' : `${övriga} andra hanteringar`} finns kvar. Hanteringen raderas inte, den slutar bara gälla.`,
      okText: 'Ångra',
      cancelText: 'Avbryt',
      okButtonProps: { danger: true },
      onOk: () => retract(h.id),
    })
  }

  const detaljer = (h: FlagHandlingRow) => (
    <div style={{ maxWidth: 260 }}>
      {!!h.sub_reasons?.length && (
        <>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Skäl</div>
          {h.sub_reasons.map(reasonLabel).map((s, i) => (
            <div key={i}>{s}</div>
          ))}
        </>
      )}

      {/* Att hanteringen är en bulk hör hemma här, inte på raden — raderna är
          budgeterade till två och det här gäller bara ibland. */}
      {h.antal_flaggor > 1 && (
        <>
          <div style={{ fontWeight: 500, margin: '12px 0 6px' }}>Omfattning</div>
          <div>Gäller {h.antal_flaggor} flaggor</div>
        </>
      )}

      {/* Kommentaren är valfri och tom oftare än inte. */}
      {!!h.kommentar && (
        <>
          <div style={{ fontWeight: 500, margin: '12px 0 6px' }}>Kommentar</div>
          <div>{h.kommentar}</div>
        </>
      )}

      {/* Uppföljningar går inte att ångra — de har en livscykel och stängs i stället,
          och backend avvisar dem. Utan spärren erbjuds en knapp som bara kan misslyckas. */}
      {h.kind !== 'uppföljning' && (
        <div style={{ marginTop: 12 }}>
          <Button danger size="small" loading={retracting === h.id} onClick={() => bekräftaÅngra(h)}>
            Ångra hantering
          </Button>
        </div>
      )}
    </div>
  )

  const onHandled = async () => {
    setOpen(false)
    await queryClient.invalidateQueries(nyckel)
    await queryClient.invalidateQueries(['cases'])
  }

  return (
    <>
      <Title level={5}>Hantering</Title>

      {hanteringar?.length ? (
        hanteringar.map((h) => (
          <div key={h.id} style={{ marginBottom: 16 }}>
            {/* Två rader, inte en: i en spalt på 25 % klipps "Hanterad 2026-08-12 av
                Anton Johansson" mitt i namnet. */}
            <div>
              <Text strong>
                {KIND_LABEL[h.kind] ?? h.kind} {moment(h.hanterad).format('YYYY-MM-DD HH:mm')}
              </Text>
            </div>
            <div>
              <Text type="secondary">Av {h.hanterad_av}</Text>
            </div>
            {/* Ingen länk om rutan skulle bli tom: en gammal uppföljning utan kommentar
                har varken skäl att visa eller en Ångra-knapp att erbjuda. */}
            {(!!h.sub_reasons?.length || !!h.kommentar || h.kind !== 'uppföljning') && (
              <Popover content={detaljer(h)} placement="leftTop" trigger="click" open={öppenDetalj === h.id} onOpenChange={(v) => setÖppenDetalj(v ? h.id : null)}>
                <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
                  Detaljer
                </Button>
              </Popover>
            )}
          </div>
        ))
      ) : (
        <Text type="secondary">Flaggan är inte hanterad.</Text>
      )}

      <Button type="primary" block onClick={() => setOpen(true)} style={{ marginTop: 16 }}>
        Hantera flagga
      </Button>

      <HandleFlagModal
        open={open}
        rows={[{ key: parseInt(flagId), flagga: parseInt(flagType), flaggnamn: '', namn: targetNamn ?? '', nivå: nivå ?? 0 }]}
        onClose={() => setOpen(false)}
        onDone={onHandled}
      />
    </>
  )
}

export default FlagHandlings
